import { Injectable, PLATFORM_ID, computed, effect, inject, isDevMode, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DivisionBrandingService } from './division-branding.service';
import { ThemeService } from './theme.service';
import {
  brandRecordFromDivision,
  type BrandConfig,
  type BrandDepth,
  type BrandRecord,
} from '../models/brand-record.model';
import { deriveBrandRecordTokens, type BrandTokenAudit } from '../utils/brand-record-tokens';
import { isValidHex, type PaperKind } from '../utils/brand-color';
import { WORKPLACE_INK_HEX } from '../constants/brand-presets';

/**
 * Brand Config Service — HANDOFF §3.
 *
 * RESPONSIBILITY: resolve the active `BrandRecord`, derive its custom
 * properties for the paper currently in use, and write them. It is an
 * EXTENSION of `DivisionBrandingService`, not a replacement: that service
 * still owns the ink axis (`--brand-color` and its derived tints, borders and
 * hovers, all of it running through the 846-test `deriveBrandTokens`). This
 * one adds the axes §3 asks for that a single hex cannot carry — a separated
 * action colour, shape, type, the weight map, the icon stroke and voice.
 *
 * SIGNALS:
 *   config  — BrandConfig { record, depth }, resolved
 *   record  — the active BrandRecord
 *   depth   — 'accent' | 'full'
 *   paper   — 'light' | 'dark', the paper the tokens were derived for
 *   audit   — BrandTokenAudit, the measured contrasts for the active record
 *
 * USED BY: bootstrapped at root; the brand test page drives the override.
 *
 * BACKEND CONTRACT: none. Reads `DivisionBrandingService.branding()`, which
 *   reads `myDivisionPath` / `myCompany`.
 *
 * AI CONTEXT — three things that are easy to get wrong here:
 *
 *  1. DEPTH IS NOT A FEATURE FLAG. `accent` still applies §3.1's separated
 *     action; it just does not touch paper, shape, type or voice. Both depths
 *     have to look deliberate, so `accent` is the neutral record's values
 *     rather than a code path that skips the service.
 *
 *  2. PROPERTIES MUST BE REMOVED, NOT OVERWRITTEN. Switching from a record
 *     that sets `--radius: 4px` to one that does not must restore 16px, and
 *     an overwrite-only loop leaves the last tenant's shape behind. The
 *     previous key set is tracked and cleared. `--fw-*` is the one exception,
 *     because `DivisionBrandingService` writes those too — see `WEIGHT_PROPS`
 *     and the clear loop.
 *
 *  3. TOKENS CASCADE, ASSETS DO NOT. `markOn(paper)` exists for the same
 *     reason `DivisionBrandingService.logoOn(paper)` does — a re-papered
 *     subtree re-resolves every custom property automatically but keeps the
 *     asset a TypeScript expression picked somewhere else.
 */
/**
 * The three properties this service shares with `DivisionBrandingService`.
 * See the clear loop in the constructor for why sharing them is deliberate.
 */
const WEIGHT_PROPS: ReadonlySet<string> = new Set([
  '--fw-medium',
  '--fw-semibold',
  '--fw-bold',
]);

/** The separated action — written only under a preview; see the effect. */
const ACTION_PROPS: ReadonlySet<string> = new Set([
  '--brand-action',
  '--brand-on-action',
  '--brand-action-hover',
]);

@Injectable({ providedIn: 'root' })
export class BrandConfigService {
  private readonly divisionBranding = inject(DivisionBrandingService);
  private readonly themeService = inject(ThemeService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Keys written on the last pass, so the next pass can clear them. */
  private written = new Set<string>();

  /** Dev/QA override — the brand test page and specs. Never set in product. */
  private readonly _override = signal<BrandConfig | null>(null);

  private readonly _depth = signal<BrandDepth>('accent');

  /**
   * The record the platform can build from what it actually stores.
   *
   * Everything §3 asks for beyond these fields resolves to the neutral
   * default, because the division document has nowhere to put it. That is
   * not a bug in this service — see the `[GAP]` markers on `BrandRecord`.
   */
  private readonly divisionRecord = computed<BrandRecord>(() => {
    const b = this.divisionBranding.branding();
    const primary = b.primaryColor ?? '';
    return brandRecordFromDivision({
      name: b.name,
      primaryColor: isValidHex(primary) ? primary : WORKPLACE_INK_HEX,
      logo: b.logo,
      logoDark: b.logoDark,
      logoLight: b.logoLight,
      banner: b.banner,
    });
  });

  readonly config = computed<BrandConfig>(() =>
    this._override() ?? { record: this.divisionRecord(), depth: this._depth() },
  );

  readonly record = computed(() => this.config().record);
  readonly depth = computed(() => this.config().depth);

  /** The paper the tokens are derived for. Only two ship; both are audited. */
  readonly paper = computed<PaperKind>(() => (this.themeService.isDark() ? 'dark' : 'light'));

  private readonly derived = computed(() =>
    deriveBrandRecordTokens(this.record(), this.paper(), this.depth()),
  );

  /** Measured contrasts for the active record on the active paper. */
  readonly audit = computed<BrandTokenAudit>(() => this.derived().audit);

  /**
   * The mark for a SPECIFIC paper, falling back through the tenant's generic
   * asset and finally to null, at which point the caller letters the name.
   *
   * A supplied mark is usually drawn for one paper — all three of the real
   * tenant assets we have are single-paper, two light and one dark — so the
   * fallback chain is load-bearing, not defensive.
   */
  markOn(paper: PaperKind | null): string | null {
    const m = this.record().mark;
    const dark = paper ? paper === 'dark' : this.themeService.isDark();
    return (dark ? m.assetOnDark : m.assetOnLight) ?? m.asset ?? null;
  }

  /** Two-letter fallback when no asset resolves on this paper. */
  readonly markLetters = computed(() => {
    const n = this.record().shortName || this.record().name;
    return n.slice(0, 2);
  });

  setDepth(depth: BrandDepth): void {
    this._depth.set(depth);
  }

  /**
   * Apply a full record app-wide. Also pushes the record's ink through
   * `DivisionBrandingService` so the accent axis — which owns the 846-test
   * derivation — stays the single source for `--brand-color`.
   */
  setOverride(config: BrandConfig): void {
    this._override.set(config);

    // Each slot is resolved to a concrete value before it is handed over.
    //
    // `DivisionBrandingService.branding()` composes every field with `??`, so
    // a null on the override falls THROUGH to the division's own value
    // rather than clearing it. Passing the record's raw `assetOnDark: null`
    // therefore does not mean "this tenant has no dark mark" — it means
    // "use the division's", and the previous tenant's logo kept appearing
    // under a brand that never supplied one. Collapsing the record's own
    // fallback chain here makes the override total for any record that
    // supplies at least one asset.
    //
    // A record with NO asset at all still cannot say so: `??` has no way to
    // express an explicit none. That is a limitation of the preview seam,
    // not of the record — see the report.
    const m = config.record.mark;
    const anyAsset = m.asset ?? m.assetOnLight ?? m.assetOnDark;

    this.divisionBranding.setPreviewOverride({
      primaryColor: config.record.ink,
      name: config.record.name,
      logo: anyAsset,
      logoDark: m.assetOnDark ?? m.asset ?? anyAsset,
      logoLight: m.assetOnLight ?? m.asset ?? anyAsset,
    });
    this.persist(config);
  }

  clearOverride(): void {
    this._override.set(null);
    this.divisionBranding.clearPreviewOverride();
    this.persist(null);
  }

  /**
   * The override survives a navigation so a tenant can be checked on Home,
   * Discover and the shell bar rather than only on the page that set it —
   * which is the whole point of §3's "three tenants, three products" claim.
   *
   * A serialised RECORD, not a fixture id: product code must never be able
   * to resolve a tenant by name. Session-scoped and dev-only, so it cannot
   * outlive the tab or reach a customer.
   */
  private static readonly PREVIEW_KEY = 'im-brand-preview';

  private persist(config: BrandConfig | null): void {
    if (!this.isBrowser || !isDevMode()) return;
    try {
      if (config) {
        sessionStorage.setItem(BrandConfigService.PREVIEW_KEY, JSON.stringify(config));
      } else {
        sessionStorage.removeItem(BrandConfigService.PREVIEW_KEY);
      }
    } catch {
      // Session storage can be unavailable (private mode, quota). A preview
      // that does not survive navigation is a degraded dev affordance, not
      // a product failure — the in-memory signal still works on this page.
    }
  }

  private restore(): BrandConfig | null {
    if (!isDevMode()) return null;
    try {
      const raw = sessionStorage.getItem(BrandConfigService.PREVIEW_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as BrandConfig;
      return parsed?.record?.ink && isValidHex(parsed.record.ink) ? parsed : null;
    } catch {
      return null; // corrupt payload — fall through to the division record
    }
  }

  constructor() {
    if (!this.isBrowser) return;

    const restored = this.restore();
    if (restored) this.setOverride(restored);

    effect(() => {
      // The action trio belongs to the accent now: styles.scss bridges
      // --brand-action to --accent-fill, which follows the person's Accent
      // setting (AccentService). An inline value here would outrank that
      // bridge, so only a preview — the brand test page, where a record can
      // separate its action from its ink — writes it.
      const derived = this.derived().props;
      const props = this._override()
        ? derived
        : Object.fromEntries(Object.entries(derived).filter(([key]) => !ACTION_PROPS.has(key)));
      const record = this.record();
      const full = this.depth() === 'full';
      const root = document.documentElement;

      // Clear anything the previous record set and this one does not. An
      // overwrite-only loop leaves the last tenant's 4px radius behind.
      for (const key of this.written) {
        if (key in props) continue;
        // …except the weight scale, which has a SECOND writer.
        // `DivisionBrandingService` owns `--fw-*` at accent depth (the weight
        // axis rides with the ink axis) and its effect is created first, so
        // it has already written the authoritative value — a real scale, or a
        // removal — by the time this loop runs. Removing here would undo that
        // on every full → accent switch and drop the brand back to the
        // stylesheet's 500/600/700 with nothing to trigger a re-apply.
        // Overwrite at full depth, never remove.
        if (WEIGHT_PROPS.has(key)) continue;
        root.style.removeProperty(key);
      }

      for (const [key, value] of Object.entries(props)) {
        root.style.setProperty(key, value);
      }
      this.written = new Set(Object.keys(props));

      // §1.7 — the paper takes a little of the brand hue. This is the single
      // biggest lever and the one difference registered before any copy is
      // read, so it is applied to the real canvas rather than an extra token
      // nothing paints with. `--brand-tint` already holds the same value and
      // the action colour was measured against it, not against bare paper.
      if (full && record.tint > 0) {
        root.style.setProperty('--surface-base', props['--brand-tint']);
        this.written.add('--surface-base');
      } else {
        root.style.removeProperty('--surface-base');
      }

      // The icon stroke needs a scoped hook because `paper-icon` renders
      // `stroke-width` as an attribute and does not read a token. Gating the
      // override on an attribute keeps every icon at its authored weight
      // unless a brand has genuinely asked for a different one.
      if (full && record.shape.iconStroke !== 2) {
        root.setAttribute('data-brand-stroke', '');
      } else {
        root.removeAttribute('data-brand-stroke');
      }
    });
  }
}
